import { useEffect } from "react";
import { useParams } from "react-router-dom";
import ArticleNavbar from "@/components/feature/ArticleNavbar";
import JsonLd from "@/components/base/JsonLd";
import { articles } from "@/mocks/articles";
import type { ArticleSection } from "@/mocks/articles";

const SITE_URL = "https://sugargoosheet.my";
const SITE_NAME = "Sugargoo Spreadsheet";
const PUBLISHER_LOGO = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg";

function ArticleContent({ section }: { section: ArticleSection }) {
  switch (section.type) {
    case "heading":
      return (
        <section className="mb-10">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
            {section.heading}
          </h2>
          {section.body && (
            <p className="text-sm text-foreground-600 leading-relaxed">{section.body}</p>
          )}
        </section>
      );

    case "text":
      return (
        <section className="mb-10">
          {section.heading && (
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
              {section.heading}
            </h2>
          )}
          {section.body && (
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">{section.body}</p>
          )}
        </section>
      );

    case "image":
      return (
        <section className="mb-10">
          <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden">
            {section.imageUrl && (
              <img
                src={section.imageUrl}
                alt={section.imageAlt || ""}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </section>
      );

    case "cards":
      return (
        <section className="mb-10">
          {section.heading && (
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
              {section.heading}
            </h2>
          )}
          {section.body && (
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">{section.body}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            {section.items?.map((item) => (
              <div
                key={item.title}
                className="p-4 rounded-lg bg-background-100 border border-background-200/60"
              >
                {item.title && (
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>
                )}
                {item.desc && (
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      );

    case "list":
      return (
        <section className="mb-10">
          {section.heading && (
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
              {section.heading}
            </h2>
          )}
          {section.body && (
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">{section.body}</p>
          )}
          <div className="flex flex-col gap-4">
            {section.items?.map((item) => (
              <div
                key={item.step || item.title}
                className="flex gap-4 p-4 rounded-lg bg-background-100 border border-background-200/60"
              >
                {item.step && (
                  <div className="w-9 h-9 flex-shrink-0 rounded-full bg-primary-500 text-white flex items-center justify-center">
                    <span className="text-xs font-bold">{item.step}</span>
                  </div>
                )}
                <div>
                  {item.title && (
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>
                  )}
                  {item.desc && (
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      );

    case "table":
      return (
        <section className="mb-10">
          {section.heading && (
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
              {section.heading}
            </h2>
          )}
          {section.body && (
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">{section.body}</p>
          )}
          <div className="rounded-lg border border-background-200/70 overflow-hidden">
            <table className="w-full">
              {section.headers && (
                <thead>
                  <tr className="bg-background-100">
                    {section.headers.map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-foreground-700">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {section.rows?.map((row, i) => (
                  <tr key={i} className="border-t border-background-200/40">
                    {row.map((cell, j) => (
                      <td key={j} className="px-4 py-2.5 text-xs text-foreground-600">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );

    case "tips":
      return (
        <section className="mb-10">
          {section.heading && (
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
              {section.heading}
            </h2>
          )}
          <div className="flex flex-col gap-4">
            {section.tips?.map((tip, i) => (
              <div
                key={i}
                className="p-5 rounded-lg bg-background-100 border border-background-200/60"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-lightbulb-line text-sm"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900">{tip.tip}</h4>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{tip.body}</p>
              </div>
            ))}
          </div>
        </section>
      );

    case "faq":
      return (
        <section className="mb-10">
          {section.heading && (
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
              {section.heading}
            </h2>
          )}
          <div className="flex flex-col gap-3">
            {section.faqs?.map((faq, i) => (
              <div key={i} className="rounded-lg border border-background-200/70 p-4">
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">{faq.q}</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      );

    case "cta":
      return (
        <section className="mb-10 p-6 md:p-8 rounded-lg bg-primary-50 border border-primary-200">
          <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-3">
            Ready to Start Using Sugargoo Spreadsheet?
          </h2>
          <p className="text-sm text-foreground-700 leading-relaxed mb-5">
            Now that you understand how Sugargoo Spreadsheet works, it is time to put that knowledge into action. Visit our main website to browse all 14 product categories and start building your first haul with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={section.ctaExternal ? section.ctaUrl || "https://kaspreadsheet.com" : section.ctaUrl || "/"}
              {...(section.ctaExternal ? { target: "_blank", rel: "noopener nofollow" } : {})}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white hover:bg-primary-600 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              {section.ctaText || "Get Started"}
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-50 text-foreground-700 hover:bg-background-100 border border-background-200/70 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              Back to Homepage
            </a>
          </div>
        </section>
      );

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) {
    return (
      <>
        <ArticleNavbar />
        <main className="pt-20 md:pt-24">
          <div className="py-20 px-4 text-center">
            <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white hover:bg-primary-600 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              Back to Home
            </a>
          </div>
        </main>
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.metaDescription,
    "image": article.heroImage,
    "datePublished": "2026-08-06",
    "dateModified": "2026-08-06",
    "author": {
      "@type": "Organization",
      "name": SITE_NAME,
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": PUBLISHER_LOGO
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}/article/${article.slug}`
    }
  };

  return (
    <>
      <JsonLd data={articleSchema} />
      <ArticleNavbar />
      <main className="pt-20 md:pt-24">
        <article className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <nav className="mb-8">
              <a
                href="/"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </a>
            </nav>

            <header className="mb-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
                  {article.category}
                </span>
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.badge}
                </span>
                <span className="text-xs text-foreground-500">{article.date}</span>
              </div>

              <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-base text-foreground-600 leading-relaxed">
                {article.metaDescription}
              </p>
            </header>

            <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden mb-10">
              <img
                src={article.heroImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom">
              {article.sections.map((section, i) => (
                <ArticleContent key={i} section={section} />
              ))}
            </div>
          </div>
        </article>
      </main>

      <footer className="py-10 px-4 md:px-6 bg-secondary-100 border-t border-background-200/70">
        <div className="max-w-3xl mx-auto text-center">
          <a href="/" className="inline-flex items-center gap-2 mb-4 cursor-pointer">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-database-2-line text-white text-sm"></i>
            </div>
            <span className="text-sm font-semibold font-heading text-foreground-950">Sugargoo Spreadsheet</span>
          </a>
          <p className="text-xs text-foreground-500">
            &copy; 2026 sugargoosheet.my. All rights reserved. This is an independent resource website.
          </p>
        </div>
      </footer>
    </>
  );
}